import { toaster } from 'rsuite';
import React from 'react';

class ToasterManager {
  // Last toast timestamp
  private lastToastTimestamp = 0;
  // Minimum delay between toasts (ms)
  private minDelay = 300;

  constructor() {
    // Clear any existing toasts on initialization
    toaster.clear();
  }

  // Check if a toast with the same text is already visible in the DOM
  private isDuplicateToast(message: React.ReactNode): boolean {
    // Extract text from the message
    let messageText = '';

    if (React.isValidElement(message)) {
      const props = message.props || {};
      if (props.children && typeof props.children === 'string') {
        messageText = props.children;
      } else if (props.text) {
        messageText = props.text;
      } else if (props.title) {
        messageText = props.title;
      }
    } else {
      messageText = String(message);
    }

    // If we couldn't extract text, can't check for duplicates
    if (!messageText) return false;

    // Check if any toast with this text is already in the DOM
    const toastElements = document.querySelectorAll('.rs-message-body');
    for (let i = 0; i < toastElements.length; i++) {
      const toastElement = toastElements[i];
      if (toastElement.textContent && toastElement.textContent.includes(messageText)) {
        console.log('Duplicate toast found in DOM:', messageText);
        return true;
      }
    }

    return false;
  }

  // Basic method to show a toast with timing control
  public push(message: React.ReactNode, config: any = {}): string {
    // Generate a unique ID for this toast
    const id = `toast-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`;

    // Check for duplicates (except for success messages)
    if (this.isDuplicateToast(message) && config.type !== 'success') {
      console.log('Suppressing duplicate toast');
      return id;
    }

    // Ensure duration is set (default to 8000ms if not specified)
    const finalConfig = {
      ...config,
      duration: config.duration !== undefined ? config.duration : 8000,
      placement: config.placement || 'topEnd'
    };

    // Calculate when to show the toast
    const now = Date.now();
    const nextToastTime = Math.max(now, this.lastToastTimestamp + this.minDelay);
    const delay = nextToastTime - now;

    // Update the last toast timestamp
    this.lastToastTimestamp = nextToastTime;

    // If there's a delay, schedule the toast
    if (delay > 0) {
      setTimeout(() => {
        toaster.push(message, finalConfig);
      }, delay);
    } else {
      // Show the toast immediately
      toaster.push(message, finalConfig);
    }

    return id;
  }

  // Standard toast types
  public success(message: React.ReactNode, config: any = {}): string {
    return this.push(message, { ...config, type: 'success' });
  }

  public error(message: React.ReactNode, config: any = {}): string {
    return this.push(message, { ...config, type: 'error' });
  }

  public warning(message: React.ReactNode, config: any = {}): string {
    return this.push(message, { ...config, type: 'warning' });
  }

  public info(message: React.ReactNode, config: any = {}): string {
    return this.push(message, { ...config, type: 'info' });
  }

  // Direct methods (immediate display)
  public successDirect(message: React.ReactNode, config: any = {}): string {
    return this.push(message, { ...config, type: 'success' });
  }

  public errorDirect(message: React.ReactNode, config: any = {}): string {
    return this.push(message, { ...config, type: 'error' });
  }

  public warningDirect(message: React.ReactNode, config: any = {}): string {
    return this.push(message, { ...config, type: 'warning' });
  }

  public infoDirect(message: React.ReactNode, config: any = {}): string {
    return this.push(message, { ...config, type: 'info' });
  }

  // Methods that don't clear existing toasts
  public successWithoutClearing(message: React.ReactNode, config: any = {}): string {
    return this.push(message, { ...config, type: 'success' });
  }

  public errorWithoutClearing(message: React.ReactNode, config: any = {}): string {
    return this.push(message, { ...config, type: 'error' });
  }

  public warningWithoutClearing(message: React.ReactNode, config: any = {}): string {
    return this.push(message, { ...config, type: 'warning' });
  }

  public infoWithoutClearing(message: React.ReactNode, config: any = {}): string {
    return this.push(message, { ...config, type: 'info' });
  }

  // For compatibility with existing code
  public showDirectToast(message: React.ReactNode, config: any = {}): string {
    return this.push(message, config);
  }

  public addWithoutClearing(message: React.ReactNode, config: any = {}): string {
    return this.push(message, config);
  }

  // Clear all toasts
  public clearAll(): void {
    toaster.clear();
  }

  // For compatibility with existing code
  public remove(id: string): void {
    // rsuite doesn't support removing by ID, so we just clear all
    this.clearAll();
  }

  public getActiveCount(): number {
    // Count toasts in the DOM
    const count = document.querySelectorAll('.rs-message').length;
    return count;
  }
}

// Export singleton instance
export const toasterManager = new ToasterManager();

